// Core imports
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Card, Col, Row, Skeleton, Statistic, Typography, Empty, Button, message } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';

// Misc imports
import api from '../api';
import SEO from '../components/SEO/SEO';
import SupportPrompt from '../components/Misc/SupportPrompt';
import { SUPPORT_EMAIL } from '../constants';

const { Title, Paragraph } = Typography;

// Harmonious color palette derived from the app's theme
const CHART_COLORS = [
  '#7c83fc', // soft purple (from primary-1)
  '#e26bf5', // soft magenta (secondary-2)
  '#36d7b7', // teal (from success-1)
  '#ffb347', // warm amber (from warning-1)
  '#f06292', // rose (from danger-1)
  '#78909c', // blue-grey
  '#b39ddb', // lavender
  '#4dd0e1', // cyan
  '#aed581', // lime
  '#ff8a65' // coral
];

const YES_NO_COLORS = {
  yes: '#36d7b7',
  no: '#f06292',
  other: '#ffb347'
};

const NDE_TYPE_COLORS = {
  peaceful: '#36d7b7',
  distressing: '#f06292',
  neutral: '#90a4ae',
  mixed: '#ffb347'
};

// Label lookup maps by category for translating enum values
const LABEL_CATEGORIES = {
  type: '_type',
  entityTypes: '_entityTypes',
  timePerception: '_timePerceptions',
  feltJudged: '_feltJudged',
  environment: '_environments',
  realityPerception: '_realityPerceptions'
};

const formatLabel = (label, category, t) => {
  if (!label) {
    return label;
  }
  const suffix = LABEL_CATEGORIES[category];
  if (suffix) {
    const translated = t(`${label}${suffix}`, '');
    if (translated) {
      return translated;
    }
  }
  const simple = t(label, '');
  if (simple) {
    return simple;
  }
  return label
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

const MetricCardSkeleton = () => (
  <Skeleton.Button active size="large" style={{ width: 64 }} />
);

const StatCard = ({ title, description, children, span = { xs: 24, md: 12, xl: 8 } }) => (
  <Col {...span}>
    <Card className="mb-24 elevation" bordered={false} style={{ height: '100%' }}>
      <Title level={5} className="mb-8">{title}</Title>
      {description && (
        <Paragraph className="text-color-black-60" style={{ fontSize: 13, marginBottom: 16 }}>
          {description}
        </Paragraph>
      )}
      {children}
    </Card>
  </Col>
);

const PieChartCard = ({ title, description, data, category, t, colors = CHART_COLORS, span, loading }) => {
  const rawTotal = data?.reduce((sum, d) => sum + d.value, 0) || 0;
  const chartData = data
    ?.filter(d => d.value > 0 && (d.value / rawTotal) >= 0.01)
    .map(d => ({ name: formatLabel(d.label, category, t), value: d.value }));

  if (loading || !chartData?.length) {
    return (
      <StatCard title={title} description={description} span={span}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}>
            <Skeleton.Avatar active shape="circle" size={190} />
          </div>
        ) : (
          <Empty description="No data available" />
        )}
      </StatCard>
    );
  }

  const total = chartData.reduce((sum, d) => sum + d.value, 0);

  const getColor = (entry, index) => {
    if (category === 'type') {
      const original = data.find(d => formatLabel(d.label, category, t) === entry.name);
      if (original && NDE_TYPE_COLORS[original.label]) {
        return NDE_TYPE_COLORS[original.label];
      }
    }
    return colors[index % colors.length];
  };

  return (
    <StatCard title={title} description={description} span={span}>
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={55}
            outerRadius={95}
            paddingAngle={3}
            dataKey="value"
            label={({ name, value }) => {
              const pct = Math.round(value / total * 100);
              const shortName = name.length > 14 ? name.slice(0, 12) + '…' : name;
              return `${shortName} (${pct}%)`;
            }}
            labelLine={true}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getColor(entry, index)} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value, name) => [`${value} (${Math.round(value / total * 100)}%)`, name]}
          />
        </PieChart>
      </ResponsiveContainer>
    </StatCard>
  );
};

const HorizontalBarChartCard = ({ title, description, data, category, t, color = '#7c83fc', span, maxItems, excludeLabels, loading }) => {
  let chartData = data
    ?.filter(d => d.value > 0 && (!excludeLabels || !excludeLabels.includes(d.label)))
    .map(d => ({ name: formatLabel(d.label, category, t), value: d.value }))
    .sort((a, b) => b.value - a.value);

  if (chartData && maxItems) {
    chartData = chartData.slice(0, maxItems);
  }

  if (loading || !chartData?.length) {
    return (
      <StatCard title={title} description={description} span={span}>
        {loading ? (
          <div style={{ paddingTop: 8 }}>
            {[88, 71, 58, 46, 35, 26].map((pct, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                <Skeleton.Button active size="small" style={{ width: 110, flexShrink: 0 }} />
                <Skeleton.Button active size="small" style={{ width: `${pct}%` }} />
              </div>
            ))}
          </div>
        ) : (
          <Empty description="No data available" />
        )}
      </StatCard>
    );
  }

  const barHeight = Math.max(280, chartData.length * 36);

  return (
    <StatCard title={title} description={description} span={span}>
      <ResponsiveContainer width="100%" height={barHeight}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#d2dadd" horizontal={false} />
          <XAxis type="number" allowDecimals={false} />
          <YAxis
            dataKey="name"
            type="category"
            width={140}
            tick={{ fontSize: 12 }}
          />
          <Tooltip />
          <Bar dataKey="value" fill={color} radius={[0, 4, 4, 0]} name="Count" />
        </BarChart>
      </ResponsiveContainer>
    </StatCard>
  );
};

const YesNoChartCard = ({ title, description, data, t, span, loading }) => {
  const chartData = data
    ?.filter(d => d.value > 0)
    .map(d => ({ name: formatLabel(d.label, null, t), value: d.value }))
    .filter(d => d.name);

  if (loading || !chartData?.length) {
    return (
      <StatCard title={title} description={description} span={span}>
        {loading ? (
          <div>
            <Skeleton.Button active block style={{ height: 40, borderRadius: 10, marginBottom: 12 }} />
            <div style={{ display: 'flex', gap: 16 }}>
              <Skeleton.Button active size="small" style={{ width: 70 }} />
              <Skeleton.Button active size="small" style={{ width: 70 }} />
            </div>
          </div>
        ) : (
          <Empty description="No data available" />
        )}
      </StatCard>
    );
  }

  const total = chartData.reduce((sum, d) => sum + d.value, 0);

  return (
    <StatCard title={title} description={description} span={span}>
      <div className="statistics-yes-no-bar">
        {chartData.map((entry, i) => {
          const percent = Math.round(entry.value / total * 100);
          const isYes = entry.name === 'Yes';
          const isOther = entry.name === 'Other';
          return (
            <div
              key={i}
              className="statistics-yes-no-segment"
              style={{
                width: `${percent}%`,
                backgroundColor: isYes ? YES_NO_COLORS.yes : isOther ? YES_NO_COLORS.other : YES_NO_COLORS.no,
                minWidth: percent > 0 ? '30px' : '0'
              }}
              title={`${entry.name}: ${entry.value} (${percent}%)`}
            >
              {percent > 10 && <span>{percent}%</span>}
            </div>
          );
        })}
      </div>
      <div className="statistics-yes-no-legend">
        {chartData.map((entry, i) => {
          const isYes = entry.name === 'Yes';
          const isOther = entry.name === 'Other';
          return (
            <span key={i} className="statistics-legend-item">
              <span
                className="statistics-legend-dot"
                style={{ backgroundColor: isYes ? YES_NO_COLORS.yes : isOther ? YES_NO_COLORS.other : YES_NO_COLORS.no }}
              />
              {entry.name}: {entry.value}
            </span>
          );
        })}
      </div>
    </StatCard>
  );
};

const StatisticsScreen = () => {
  const { t } = useTranslation();
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await api.fetchStatistics();
        setStatistics(result);
      } catch (error) {
        message.error('Failed to load statistics. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (!loading && !statistics) {
    return (
      <div className="text-center py-64">
        <Empty description="Statistics could not be loaded" />
      </div>
    );
  }

  // Compute percentages for the top metric cards
  const computePercent = (distData, yesLabel = 'yes') => {
    const yes = distData?.find(d => d.label === yesLabel)?.value || 0;
    const total = distData?.reduce((s, d) => s + d.value, 0) || 0;
    return total > 0 ? `${Math.round(yes / total * 100)}%` : 'N/A';
  };

  const lifeReviewPercent = computePercent(statistics?.hadLifeReview);
  const brightLightPercent = computePercent(statistics?.sawBrightLight);
  const choiceToReturnPercent = computePercent(statistics?.hadChoiceToReturn);
  const healedPercent = computePercent(statistics?.wasHealed);

  // Distressing NDE metrics
  const distressingHellishPercent = computePercent(statistics?.distressingSawHellishImagery);
  const distressingEscapedPercent = computePercent(statistics?.distressingEscapedNegativeNde);
  const distressingLifeReviewPercent = computePercent(statistics?.distressingHadLifeReview);
  const distressingBrightLightPercent = computePercent(statistics?.distressingSawBrightLight);

  // Peaceful NDE metrics
  const peacefulLifeReviewPercent = computePercent(statistics?.peacefulHadLifeReview);
  const peacefulBrightLightPercent = computePercent(statistics?.peacefulSawBrightLight);
  const peacefulLostFearPercent = computePercent(statistics?.peacefulLostFearOfDeath);
  const peacefulDidNotWantToReturnPercent = computePercent(statistics?.peacefulDidNotWantToReturn);

  return (
    <div className="statistics-page">
      <SEO
        title="NDE Statistics | Near-Death Experience Data & Research"
        description="Data-driven statistics on near-death experiences: Greyson scale distributions, life review rates, out-of-body experience frequency, peaceful vs distressing NDE ratios, entity encounters, and more, all derived from verified accounts."
        path="/statistics"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Dataset',
            name: 'Near-Death Experience Statistics | NDE Archive',
            description: 'Statistical analysis of near-death experiences including NDE type distributions, Greyson scale scores, life review rates, out-of-body experience frequency, entity encounter rates, and before/after belief changes. Derived from verified public accounts.',
            url: `${process.env.REACT_APP_SITE_URL || 'https://ndearchive.com'}/statistics`,
            creator: {
              '@type': 'Organization',
              name: 'NDE Archive',
              url: process.env.REACT_APP_SITE_URL || 'https://ndearchive.com'
            },
            keywords: [
              'near-death experience statistics',
              'NDE research data',
              'Greyson scale distribution',
              'life review statistics',
              'out-of-body experience rates',
              'distressing NDE frequency',
              'peaceful near-death experience',
              'NDE entity encounters',
              'clinical death research'
            ],
            isAccessibleForFree: true
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: process.env.REACT_APP_SITE_URL || 'https://ndearchive.com' },
              { '@type': 'ListItem', position: 2, name: 'NDE Statistics', item: `${process.env.REACT_APP_SITE_URL || 'https://ndearchive.com'}/statistics` }
            ]
          }
        ]}
      />

      <Row className="mb-32">
        <Col span={24}>
          <Title level={1} style={{ fontWeight: 800 }}>NDE Statistics</Title>
          <Paragraph className="text-color-black-80" style={{ fontSize: 18 }}>
            A comprehensive overview of near-death experience data collected from public submissions.
            {statistics && (<> These statistics are derived from {statistics.publicCount} publicly shared NDEs.</>)}
          </Paragraph>
        </Col>
      </Row>

      {/* Key metrics */}
      <Row gutter={[24, 24]} className="mb-32">
        <Col xs={12} sm={6}>
          <Card className="elevation statistics-metric-card" bordered={false} style={{ background: 'linear-gradient(135deg, rgba(124,131,252,0.14) 0%, rgba(124,131,252,0.04) 100%)', borderTop: '3px solid #7c83fc' }}>
            <Statistic title="Had Life Review" value={loading ? ' ' : lifeReviewPercent} valueStyle={{ color: '#7c83fc' }} formatter={loading ? () => <MetricCardSkeleton /> : undefined} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="elevation statistics-metric-card" bordered={false} style={{ background: 'linear-gradient(135deg, rgba(54,215,183,0.14) 0%, rgba(54,215,183,0.04) 100%)', borderTop: '3px solid #36d7b7' }}>
            <Statistic title="Saw Bright Light" value={loading ? ' ' : brightLightPercent} valueStyle={{ color: '#36d7b7' }} formatter={loading ? () => <MetricCardSkeleton /> : undefined} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="elevation statistics-metric-card" bordered={false} style={{ background: 'linear-gradient(135deg, rgba(255,179,71,0.14) 0%, rgba(255,179,71,0.04) 100%)', borderTop: '3px solid #ffb347' }}>
            <Statistic title="Had Choice to Return" value={loading ? ' ' : choiceToReturnPercent} valueStyle={{ color: '#ffb347' }} formatter={loading ? () => <MetricCardSkeleton /> : undefined} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="elevation statistics-metric-card" bordered={false} style={{ background: 'linear-gradient(135deg, rgba(226,107,245,0.14) 0%, rgba(226,107,245,0.04) 100%)', borderTop: '3px solid #e26bf5' }}>
            <Statistic title="Healed of Ailment" value={loading ? ' ' : healedPercent} valueStyle={{ color: '#e26bf5' }} formatter={loading ? () => <MetricCardSkeleton /> : undefined} />
          </Card>
        </Col>
      </Row>

      {/* ── General Section ── */}
      <Title level={2} className="mb-16" style={{ fontWeight: 700 }}>General</Title>

      {/* NDE Type Distribution + Time Perception */}
      <Row gutter={[24, 24]} className="mb-8">
        <PieChartCard
          title="NDE Type Distribution"
          description="Classification of near-death experiences by their overall emotional character."
          data={statistics?.typeDistribution}
          category="type"
          t={t}
          span={{ xs: 24, md: 12 }}
          loading={loading}
        />
        <PieChartCard
          title="Time Perception"
          description="How experiencers perceived the passage of time during their NDE."
          data={statistics?.timePerceptionDistribution}
          category="timePerception"
          t={t}
          span={{ xs: 24, md: 12 }}
          loading={loading}
        />
      </Row>

      {/* Felt Judged + Entity Types */}
      <Row gutter={[24, 24]} className="mb-8">
        <PieChartCard
          title="Felt Judged"
          description="Whether experiencers felt a sense of judgment during their NDE, and if so, by whom."
          data={statistics?.feltJudgedDistribution}
          category="feltJudged"
          t={t}
          span={{ xs: 24, md: 12 }}
          loading={loading}
        />
        <HorizontalBarChartCard
          title="Entity Types Encountered"
          description="Types of beings or presences reported by experiencers during their NDE."
          data={statistics?.entityTypesDistribution}
          category="entityTypes"
          t={t}
          color="#7c83fc"
          span={{ xs: 24, md: 12 }}
          loading={loading}
          maxItems={10}
          excludeLabels={['other']}
        />
      </Row>

      {/* Metaphysical Effects */}
      <Title level={3} className="mb-16 mt-16">Metaphysical Effects</Title>
      <Row gutter={[24, 24]} className="mb-8">
        <YesNoChartCard
          title="Was Healed of a Physical Ailment"
          description="Among those who were suffering from a physical condition prior to their NDE, whether the ailment disappeared or improved afterwards."
          data={statistics?.wasHealed}
          t={t}
          loading={loading}
        />
        <YesNoChartCard
          title="Had Visions of the Future"
          description="Whether experiencers reported seeing or being shown future events during their NDE."
          data={statistics?.hadVisionsOfFuture}
          t={t}
          loading={loading}
        />
        <YesNoChartCard
          title="Future Visions Came True"
          description="Among those who reported future visions, whether those visions later came to pass."
          data={statistics?.visionsCameTrue}
          t={t}
          loading={loading}
        />
        <YesNoChartCard
          title="Returned With Abilities"
          description="Whether experiencers reported developing new abilities afterwards, such as intuition, sensing presences, or healing."
          data={statistics?.developedPsychicAbilities}
          t={t}
          loading={loading}
        />
      </Row>

      {/* ── Who Reports These Experiences ── */}
      <Title level={3} className="mb-16 mt-16">Who Reports These Experiences</Title>
      <Row gutter={[24, 24]}>
        <HorizontalBarChartCard
          title="Countries Represented"
          description="Where experiencers were located. Heavily weighted towards English-speaking countries, which reflects where the source archives collect accounts rather than where NDEs occur."
          data={statistics?.countryDistribution}
          t={t}
          color="#7c83fc"
          span={{ xs: 24, md: 12 }}
          loading={loading}
          maxItems={12}
        />
        <HorizontalBarChartCard
          title="Gender"
          description="Gender of experiencers, where the account records it."
          data={statistics?.genderDistribution}
          t={t}
          color="#e26bf5"
          span={{ xs: 24, md: 12 }}
          loading={loading}
          excludeLabels={['other']}
        />
      </Row>

      {/* ── Peaceful NDEs Section ── */}
      <div
        className="mt-32 mb-16"
        style={{
          borderLeft: '4px solid #36d7b7',
          paddingLeft: 16
        }}
      >
        <Title level={2} style={{ fontWeight: 700, marginBottom: 4 }}>Peaceful NDEs</Title>
        <Paragraph className="text-color-black-60" style={{ fontSize: 15, marginBottom: 0 }}>
          Statistics filtered to only include peaceful near-death experiences.
          {statistics && statistics.peacefulCount > 0 && (
            <> Based on {statistics.peacefulCount} peaceful NDEs in the dataset.</>
          )}
        </Paragraph>
      </div>

      {/* Peaceful key metrics */}
      <Row gutter={[24, 24]} className="mb-24">
        <Col xs={12} sm={6}>
          <Card className="elevation statistics-metric-card" bordered={false} style={{ background: 'linear-gradient(135deg, rgba(124,131,252,0.14) 0%, rgba(124,131,252,0.04) 100%)', borderTop: '3px solid #7c83fc' }}>
            <Statistic title="Had Life Review" value={loading ? ' ' : peacefulLifeReviewPercent} valueStyle={{ color: '#7c83fc' }} formatter={loading ? () => <MetricCardSkeleton /> : undefined} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="elevation statistics-metric-card" bordered={false} style={{ background: 'linear-gradient(135deg, rgba(54,215,183,0.14) 0%, rgba(54,215,183,0.04) 100%)', borderTop: '3px solid #36d7b7' }}>
            <Statistic title="Saw Bright Light" value={loading ? ' ' : peacefulBrightLightPercent} valueStyle={{ color: '#36d7b7' }} formatter={loading ? () => <MetricCardSkeleton /> : undefined} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="elevation statistics-metric-card" bordered={false} style={{ background: 'linear-gradient(135deg, rgba(255,179,71,0.14) 0%, rgba(255,179,71,0.04) 100%)', borderTop: '3px solid #ffb347' }}>
            <Statistic title="Lost Fear of Death" value={loading ? ' ' : peacefulLostFearPercent} valueStyle={{ color: '#ffb347' }} formatter={loading ? () => <MetricCardSkeleton /> : undefined} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="elevation statistics-metric-card" bordered={false} style={{ background: 'linear-gradient(135deg, rgba(226,107,245,0.14) 0%, rgba(226,107,245,0.04) 100%)', borderTop: '3px solid #e26bf5' }}>
            <Statistic title="Didn't Want to Return" value={loading ? ' ' : peacefulDidNotWantToReturnPercent} valueStyle={{ color: '#e26bf5' }} formatter={loading ? () => <MetricCardSkeleton /> : undefined} />
          </Card>
        </Col>
      </Row>

      {/* Emotions + Entity Types */}
      <Row gutter={[24, 24]} className="mb-8">
        <HorizontalBarChartCard
          title="Emotions Reported"
          description="The emotions reported by peaceful NDE experiencers during their experience."
          data={statistics?.peacefulEmotions}
          t={t}
          color="#36d7b7"
          span={{ xs: 24, md: 12 }}
          loading={loading}
          maxItems={12}
          excludeLabels={['other']}
        />
        <HorizontalBarChartCard
          title="Entity Types Encountered"
          description="Types of beings or presences reported by peaceful NDE experiencers."
          data={statistics?.peacefulEntityTypes}
          category="entityTypes"
          t={t}
          color="#7c83fc"
          span={{ xs: 24, md: 12 }}
          loading={loading}
          maxItems={10}
          excludeLabels={['other']}
        />
      </Row>

      {/* Environments + Reality Perception */}
      <Row gutter={[24, 24]} className="mb-8">
        <HorizontalBarChartCard
          title="Environments Reported"
          description="The settings or realms described by peaceful NDE experiencers during their experience."
          data={statistics?.peacefulEnvironment}
          category="environment"
          t={t}
          color="#36d7b7"
          span={{ xs: 24, md: 12 }}
          loading={loading}
          maxItems={10}
          excludeLabels={['other']}
        />
        <PieChartCard
          title="Reality Perception"
          description="How peaceful NDE experiencers described the reality of their experience compared to normal waking life."
          data={statistics?.peacefulRealityPerception}
          category="realityPerception"
          t={t}
          span={{ xs: 24, md: 12 }}
          loading={loading}
        />
      </Row>

      {/* ── Distressing NDEs Section ── */}
      <div
        className="mt-32 mb-16"
        style={{
          borderLeft: '4px solid #f06292',
          paddingLeft: 16
        }}
      >
        <Title level={2} style={{ fontWeight: 700, marginBottom: 4 }}>Distressing NDEs</Title>
        <Paragraph className="text-color-black-60" style={{ fontSize: 15, marginBottom: 0 }}>
          Statistics filtered to only include distressing near-death experiences.
          {statistics && statistics.distressingCount > 0 && (
            <> Based on {statistics.distressingCount} distressing NDEs in the dataset.</>
          )}
        </Paragraph>
      </div>

      {/* Distressing key metrics */}
      <Row gutter={[24, 24]} className="mb-24">
        <Col xs={12} sm={6}>
          <Card className="elevation statistics-metric-card" bordered={false} style={{ background: 'linear-gradient(135deg, rgba(124,131,252,0.14) 0%, rgba(124,131,252,0.04) 100%)', borderTop: '3px solid #7c83fc' }}>
            <Statistic title="Had Life Review" value={loading ? ' ' : distressingLifeReviewPercent} valueStyle={{ color: '#7c83fc' }} formatter={loading ? () => <MetricCardSkeleton /> : undefined} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="elevation statistics-metric-card" bordered={false} style={{ background: 'linear-gradient(135deg, rgba(54,215,183,0.14) 0%, rgba(54,215,183,0.04) 100%)', borderTop: '3px solid #36d7b7' }}>
            <Statistic title="Saw Bright Light" value={loading ? ' ' : distressingBrightLightPercent} valueStyle={{ color: '#36d7b7' }} formatter={loading ? () => <MetricCardSkeleton /> : undefined} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="elevation statistics-metric-card" bordered={false} style={{ background: 'linear-gradient(135deg, rgba(240,98,146,0.14) 0%, rgba(240,98,146,0.04) 100%)', borderTop: '3px solid #f06292' }}>
            <Statistic title="Saw Hellish Imagery" value={loading ? ' ' : distressingHellishPercent} valueStyle={{ color: '#f06292' }} formatter={loading ? () => <MetricCardSkeleton /> : undefined} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="elevation statistics-metric-card" bordered={false} style={{ background: 'linear-gradient(135deg, rgba(255,179,71,0.14) 0%, rgba(255,179,71,0.04) 100%)', borderTop: '3px solid #ffb347' }}>
            <Statistic title="Escaped the NDE" value={loading ? ' ' : distressingEscapedPercent} valueStyle={{ color: '#ffb347' }} formatter={loading ? () => <MetricCardSkeleton /> : undefined} />
          </Card>
        </Col>
      </Row>

      {/* Felt Deserved + Emotions */}
      <Row gutter={[24, 24]} className="mb-8">
        <PieChartCard
          title="Felt They Deserved the Experience"
          description="Whether distressing NDE experiencers reported feeling that the negative nature of their experience was deserved or justified."
          data={statistics?.distressingFeltDeservedNde}
          t={t}
          span={{ xs: 24, md: 12 }}
          loading={loading}
        />
        <HorizontalBarChartCard
          title="Emotions Reported"
          description="The emotions reported by distressing NDE experiencers during their experience."
          data={statistics?.distressingEmotions}
          t={t}
          color="#f06292"
          span={{ xs: 24, md: 12 }}
          loading={loading}
          maxItems={12}
          excludeLabels={['other']}
        />
      </Row>

      {/* Entity Types + Environments */}
      <Row gutter={[24, 24]} className="mb-8">
        <HorizontalBarChartCard
          title="Entity Types Encountered"
          description="Types of beings or presences reported by distressing NDE experiencers — benevolent vs. malevolent entities."
          data={statistics?.distressingEntityTypes}
          category="entityTypes"
          t={t}
          color="#f06292"
          span={{ xs: 24, md: 12 }}
          loading={loading}
          maxItems={10}
          excludeLabels={['other']}
        />
        <HorizontalBarChartCard
          title="Environments Reported"
          description="The settings or realms described by distressing NDE experiencers during their experience."
          data={statistics?.distressingEnvironment}
          category="environment"
          t={t}
          color="#e26bf5"
          span={{ xs: 24, md: 12 }}
          loading={loading}
          maxItems={10}
          excludeLabels={['other']}
        />
      </Row>

      {/* Request a Chart CTA */}
      <Row className="mb-32 mt-16">
        <Col xs={24}>
          <Card
            bordered={false}
            style={{
              borderRadius: 20,
              background: 'linear-gradient(135deg, rgba(145, 149, 252, 0.08), rgba(191, 193, 255, 0.08))',
              textAlign: 'center',
              padding: '32px 24px'
            }}
          >
            <Title level={4} className="mb-8">Missing a chart?</Title>
            <Paragraph className="text-color-black-80 mb-16">
              If there's a specific statistic or visualization you'd like to see, let us know and we'll consider adding it.
            </Paragraph>
            <Button
              type="primary"
              icon={<MailOutlined style={{ marginRight: 6 }} />}
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Statistics Chart Request')}&body=${encodeURIComponent('Hi,\n\nI would like to request the following chart/statistic on the Statistics page:\n\n')}`}
              style={{ borderRadius: 8 }}
            >
              Request a Chart
            </Button>
          </Card>
        </Col>
      </Row>

      <SupportPrompt variant="stats" />
    </div>
  );
};

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  children: PropTypes.node,
  span: PropTypes.object
};

PieChartCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  data: PropTypes.array,
  category: PropTypes.string,
  t: PropTypes.func.isRequired,
  colors: PropTypes.array,
  span: PropTypes.object,
  loading: PropTypes.bool
};

HorizontalBarChartCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  data: PropTypes.array,
  category: PropTypes.string,
  t: PropTypes.func.isRequired,
  color: PropTypes.string,
  span: PropTypes.object,
  maxItems: PropTypes.number,
  loading: PropTypes.bool,
  excludeLabels: PropTypes.array
};

YesNoChartCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  data: PropTypes.array,
  t: PropTypes.func.isRequired,
  span: PropTypes.object,
  loading: PropTypes.bool
};

export default StatisticsScreen;
